/**
 * Shared ENTITY EDIT-DRAWER HOST (_GLOBAL.md G6).
 *
 * FROZEN SHARED COMPONENT — owned by the foundation-integrator.
 *
 * One slide-in drawer, hosted once in the studio chrome. Clicking any
 * inline entity (a character circle, a location/asset thumbnail, an
 * `@`-ref) opens that entity's edit form IN PLACE, without navigating
 * away from the script/panel the user is in.
 *
 * Entity panels do NOT build their own drawer — they call
 * `openEntityDrawer({ title, render })` and render their existing form
 * components into the body element the drawer hands them. The Script
 * Editor's click→drawer hooks (G6) route through here too.
 *
 * Usage (panel agent):
 *
 *   import { openEntityDrawer, closeEntityDrawer } from "../shared";
 *
 *   thumbnail.addEventListener("click", () => {
 *     openEntityDrawer({
 *       title: `Edit ${character.name}`,
 *       render: (body) => body.appendChild(buildCharacterForm(character)),
 *       onClose: () => persistDraft(),
 *     });
 *   });
 *
 * main.ts wires the host once via `mountSharedEditDrawer(document.body)`;
 * if a panel opens the drawer before that wiring runs, a default host is
 * lazily created on `document.body`, so the component is safe to use
 * standalone (e.g. in a per-panel worktree before merge).
 */

import "./shared.css";

export interface EntityDrawerContent {
  /** Header title, e.g. "Edit Jax". */
  title: string;
  /** Optional sub-label under the title (entity id / type). */
  subtitle?: string;
  /**
   * Populate the scrollable drawer body. Called once on open with an
   * empty container the caller owns.
   */
  render: (body: HTMLElement) => void;
  /** Optional footer actions (Save / Delete …). Rendered right-aligned. */
  actions?: HTMLElement[];
  /** Fired after the drawer finishes closing (commit drafts here). */
  onClose?: () => void;
}

export class EntityEditDrawer {
  private readonly host: HTMLElement;
  private readonly root: HTMLDivElement;
  private readonly scrim: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly titleEl: HTMLHeadingElement;
  private readonly subtitleEl: HTMLParagraphElement;
  private readonly bodyEl: HTMLDivElement;
  private readonly footerEl: HTMLDivElement;
  private current: EntityDrawerContent | null = null;
  private readonly onKeyDown: (event: KeyboardEvent) => void;

  constructor(host: HTMLElement = document.body) {
    this.host = host;

    this.root = document.createElement("div");
    this.root.className = "studio-drawer-root";
    this.root.dataset.open = "false";
    this.root.setAttribute("aria-hidden", "true");

    this.scrim = document.createElement("div");
    this.scrim.className = "studio-drawer-scrim";
    this.scrim.addEventListener("click", () => this.close());

    this.panel = document.createElement("div");
    this.panel.className = "studio-drawer-panel";
    this.panel.setAttribute("role", "dialog");
    this.panel.setAttribute("aria-modal", "true");
    this.panel.dataset.kokoSurface = "entity-drawer";

    const header = document.createElement("div");
    header.className = "studio-drawer-header";

    const titles = document.createElement("div");
    titles.className = "studio-drawer-titles";
    this.titleEl = document.createElement("h3");
    this.titleEl.className = "studio-drawer-title";
    this.subtitleEl = document.createElement("p");
    this.subtitleEl.className = "studio-drawer-subtitle";
    titles.appendChild(this.titleEl);
    titles.appendChild(this.subtitleEl);

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "studio-drawer-close";
    closeButton.setAttribute("aria-label", "Close");
    closeButton.textContent = "×";
    closeButton.addEventListener("click", () => this.close());

    header.appendChild(titles);
    header.appendChild(closeButton);

    this.bodyEl = document.createElement("div");
    this.bodyEl.className = "studio-drawer-body";

    this.footerEl = document.createElement("div");
    this.footerEl.className = "studio-drawer-footer";

    this.panel.appendChild(header);
    this.panel.appendChild(this.bodyEl);
    this.panel.appendChild(this.footerEl);

    this.root.appendChild(this.scrim);
    this.root.appendChild(this.panel);
    this.host.appendChild(this.root);

    this.onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && this.isOpen()) {
        event.stopPropagation();
        this.close();
      }
    };
  }

  /** Open the drawer with fresh content (replaces any current content). */
  open(content: EntityDrawerContent): void {
    // If something is already open, run its onClose before swapping so a
    // draft isn't lost when the user clicks a different entity.
    if (this.current) this.current.onClose?.();
    this.current = content;

    this.titleEl.textContent = content.title;
    this.subtitleEl.textContent = content.subtitle ?? "";
    this.subtitleEl.style.display = content.subtitle ? "" : "none";

    this.bodyEl.replaceChildren();
    content.render(this.bodyEl);

    this.footerEl.replaceChildren();
    if (content.actions && content.actions.length > 0) {
      this.footerEl.style.display = "";
      for (const action of content.actions) this.footerEl.appendChild(action);
    } else {
      this.footerEl.style.display = "none";
    }

    this.root.dataset.open = "true";
    this.root.setAttribute("aria-hidden", "false");
    document.addEventListener("keydown", this.onKeyDown, true);
    // Focus the panel so Escape + tab work immediately.
    requestAnimationFrame(() => this.panel.focus?.());
  }

  close(): void {
    if (this.root.dataset.open !== "true") return;
    const closing = this.current;
    this.current = null;
    this.root.dataset.open = "false";
    this.root.setAttribute("aria-hidden", "true");
    document.removeEventListener("keydown", this.onKeyDown, true);
    // Defer body clear until the slide-out finishes so it doesn't flash.
    window.setTimeout(() => {
      if (!this.current) this.bodyEl.replaceChildren();
    }, 240);
    closing?.onClose?.();
  }

  isOpen(): boolean {
    return this.root.dataset.open === "true";
  }

  getElement(): HTMLElement {
    return this.root;
  }

  destroy(): void {
    document.removeEventListener("keydown", this.onKeyDown, true);
    this.root.remove();
  }
}

// --------------------------------------------------------------------
// Module singleton — panels call these without holding a reference.
// --------------------------------------------------------------------

let sharedDrawer: EntityEditDrawer | null = null;

/**
 * Create + mount the shared drawer host (call once from main.ts chrome).
 * Returns the instance; replaces any prior shared host.
 */
export function mountSharedEditDrawer(host: HTMLElement = document.body): EntityEditDrawer {
  if (sharedDrawer) sharedDrawer.destroy();
  sharedDrawer = new EntityEditDrawer(host);
  return sharedDrawer;
}

/** Get the shared drawer, lazily creating a default on `document.body`. */
export function getSharedEditDrawer(): EntityEditDrawer {
  if (!sharedDrawer) sharedDrawer = new EntityEditDrawer(document.body);
  return sharedDrawer;
}

/** Open the shared entity edit-drawer (G6 primary edit interaction). */
export function openEntityDrawer(content: EntityDrawerContent): void {
  getSharedEditDrawer().open(content);
}

/** Close the shared entity edit-drawer. */
export function closeEntityDrawer(): void {
  sharedDrawer?.close();
}
